import { LupaIcon } from "assets/icons/LupaIcon";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Pagination } from "components/Pagination/Pagination";
import { Select } from "components/Select/Select";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { DiseñoCurricularI } from "interfaces/DiseñoCurricular";
import React, { useEffect, useState } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDiseñosCurriculares } from "services/diseñoCurricular";
import {
  fetchEstados,
  fetchNiveles,
  fetchTiposEfecto,
  fetchTiposResoluciones,
} from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { DiseñosCurricularesTable } from "./DiseñosCurricularesTable/DiseñosCurricularesTable";
import { DiseñosCurricularesRowActions } from "./DiseñosCurricularesTable/DiseñosCurricularesRowActions";
import { DiseñosCurricularesABM } from "./DiseñosCurricularesABM/DiseñosCurricularesABM";
import { getEstados } from "services/general";

export const DiseñosCurriculares = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<DiseñoCurricularI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {
    estados,
    niveles: rawNiveles,
    loadingAppData,
  } = useSelector((st: RootState) => st.appData);
  const niveles = rawNiveles ? Object.values(rawNiveles) : [];
  const nivelesArray = rawNiveles
    ? Object.values(rawNiveles).filter((nivel) => nivel.id && nivel.nombre)
    : [];
  const [showModalABM, setShowModalABM] = useState(false);
  const [actionType, setActionType] = useState<"create" | "edit" | undefined>();
  const [selectedElement, setSelectedElement] = useState<
    DiseñoCurricularI | undefined
  >();

  const initialValues = {
    nombre: "",
    descripcion: "",
    estado_id: "",
    nivel_id: "",
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchDiseñosCurriculares = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getDiseñosCurriculares({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar los diseños curriculares");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTable = () => {
    setPage(1);
    searchDiseñosCurriculares({ filtros: { ...values, page: 1 } });
  };

  const handleCloseModal = () => {
    setShowModalABM(false);
    setTimeout(() => setActionType(undefined), 500);
  };

  const handleEdit = (elemento: DiseñoCurricularI) => {
    setActionType("edit");
    setSelectedElement(elemento);
    setShowModalABM(true);
  };

  const handleCreate = () => {
    setActionType("create");
    setShowModalABM(true);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchDiseñosCurriculares({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    searchDiseñosCurriculares({ filtros: {} });
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const renderActions = (
    elemento: DiseñoCurricularI,
    onClickShowDetail: (elemento: DiseñoCurricularI) => void,
  ) => {
    return (
      <DiseñosCurricularesRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        refreshTable={refreshTable}
        onClickShowDetail={onClickShowDetail}
      />
    );
  };

  useEffect(() => {
    searchDiseñosCurriculares({ filtros: { ...values, limit, page } });
  }, [values]);

  useEffect(() => {
    if (!estados) dispatch(fetchEstados());
    if (!rawNiveles) dispatch(fetchNiveles());
  }, []);

  console.log(nivelesArray);

  return (
    <section>
      <h1 className="section-title">Diseños curriculares</h1>

      <h2 className="section-subtitle mt-2">
        Desde acá podrás gestionar los diseños curriculares.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div className="d-flex column-gap-3">
          <Input
            size="sm"
            id="nombre"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar nombre"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Input
            size="sm"
            id="descripcion"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar descripcion"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Select
            id="estado_id"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={estados ?? []}
            isLoading={loadingAppData.estados}
            placeholder="Estado"
            width="145px"
          />
          <Select
            id="nivel_id"
            values={values}
            onChange={handleChangeWithResetPage}
            optionsFormatConfig={{ value: "id", label: "nombre" }}
            options={nivelesArray}
            isLoading={loadingAppData.niveles}
            placeholder="Nivel"
            width="145px"
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
            disabled={
              !values.nombre &&
              !values.estado_id &&
              !values.nivel_id &&
              !values.descripcion
            }
          >
            Limpiar filtros
          </Button>
        </div>
        <Button size="lg" onClick={handleCreate}>
          Nuevo diseño curricular
        </Button>
        <DiseñosCurricularesABM
          showModal={showModalABM}
          actionType={actionType}
          onClose={handleCloseModal}
          elemento={selectedElement}
          onSuccess={refreshTable}
        />
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <DiseñosCurricularesTable
            elementos={elementos}
            renderActions={renderActions}
          />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}
    </section>
  );
};
